import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useAdTracker } from "@/hooks/useAdTracker";
import { Skeleton } from "@/components/ui/skeleton";
import { ResponsiveImage } from "@/components/ResponsiveImage";

interface Ad {
  id: string;
  ad_name: string;
  company_name: string;
  offer_text: string;
  image_url: string;
  cta_url: string;
  page_placement: string;
}

interface AdvertisingBannerProps {
  placement?: "top" | "bottom" | "between";
  className?: string;
}

const AdvertisingBanner = ({ placement = "top", className = "" }: AdvertisingBannerProps) => {
  const [ads, setAds] = useState<Ad[]>([]);
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const currentAd = ads[currentAdIndex];
  
  // Track impressions using the dedicated hook
  const { trackAdView } = useAdTracker({ 
    placement, 
    adId: currentAd?.id,
    enabled: !!currentAd && isVisible 
  });

  // Fetch active ads from database
  useEffect(() => {
    const fetchAds = async () => {
      try {
        const today = new Date().toISOString().split('T')[0];
        
        const { data, error } = await supabase
          .from('ads')
          .select('id, ad_name, company_name, offer_text, image_url, cta_url, page_placement')
          .eq('is_active', true)
          .eq('page_placement', placement)
          .lte('start_date', today)
          .gte('end_date', today)
          .order('created_at', { ascending: false });

        if (error) throw error;
        
        if (data && data.length > 0) {
          setAds(data);
        } else {
          setIsVisible(false); // Hide banner if no ads available
        }
      } catch (error) {
        console.error('Error fetching ads:', error);
        setIsVisible(false);
      } finally {
        setLoading(false);
      }
    };

    fetchAds();
  }, [placement]);

  // Rotate ads every 12 seconds
  useEffect(() => {
    if (ads.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentAdIndex((prev) => (prev + 1) % ads.length);
    }, 12000);

    return () => clearInterval(interval);
  }, [ads.length]);

  // Track click and redirect
  const handleAdClick = async () => {
    if (!currentAd) return;

    try {
      // Track click via rate-limited edge function
      await supabase.functions.invoke('submit-ad-view', {
        body: {
          ad_id: currentAd.id,
          placement,
          clicked: true,
        },
      });

      // Increment clicks in ads table
      await supabase.rpc('increment_promo_stat', {
        promo_id: currentAd.id,
        field: 'clicks'
      });

      // Open advertiser URL
      window.open(currentAd.cta_url, '_blank');
    } catch (error) {
      console.error('Error tracking ad click:', error);
      // Still open URL even if tracking fails
      window.open(currentAd.cta_url, '_blank');
    }
  };

  if (!isVisible || loading || !currentAd) {
    return loading ? (
      <div className={`relative z-0 w-full max-w-full overflow-x-hidden px-2 ${className}`}>
        <Skeleton className="h-24 w-full rounded-xl" />
      </div>
    ) : null;
  }

  return (
    <div className={`relative z-0 w-full max-w-full overflow-x-hidden px-2 ${className}`}>
      <Card className="relative w-full overflow-hidden rounded-xl bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20">
        <div
          className="px-4 py-3 md:py-4 flex items-center gap-3 cursor-pointer group"
          onClick={handleAdClick}
        >
          {/* Ad Image */}
          {currentAd.image_url && (
            <ResponsiveImage 
              src={currentAd.image_url} 
              alt={currentAd.ad_name}
              width={80}
              height={80}
              className="w-16 h-16 md:w-20 md:h-20 rounded-lg flex-shrink-0 group-hover:scale-105 transition-transform"
              sizes="(max-width: 768px) 64px, 80px"
            />
          )}

          {/* Ad Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-medium px-2 py-0.5 bg-primary/20 text-primary rounded">
                Sponsored
              </span>
            </div>
            <h3 className="font-semibold text-sm md:text-base truncate text-foreground">
              {currentAd.ad_name || currentAd.company_name}
            </h3>
            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
              {currentAd.offer_text}
            </p>
          </div>

          {/* Close Button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              setIsVisible(false);
            }}
            className="hover:bg-primary/20 min-h-[44px] min-w-[44px] flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Progress Dots */}
        {ads.length > 1 && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
            {ads.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentAdIndex 
                    ? "bg-primary w-4" 
                    : "bg-primary/30"
                }`}
              />
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default AdvertisingBanner;
